import { Alert, Box, Button, Text, Tooltip } from '@factorialco/deprecated-design-system'
import {
  type LeaveEmployeeDataFragment,
  type ResourceFieldDataFragment,
  type TimeoffCurrentEmployeeDataFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { DelegateApproverSelector } from 'modules/approvals/components/DelegateApproverSelector'
import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'
import FranceCalendarWarnings from 'modules/timeoff/components/FranceCalendarWarnings'
import Content from 'modules/timeoff/components/LeaveContent'
import LeaveInfo from 'modules/timeoff/components/LeaveInfo'
import useButtonCopy from 'modules/timeoff/hooks/useButtonCopy'
import useCurrentEmployeeRole from 'modules/timeoff/hooks/useCurrentEmployeeRole'
import useCustomFields from 'modules/timeoff/hooks/useCustomFields'
import useFetchLeaveTypes from 'modules/timeoff/hooks/useFetchLeaveTypes'
import useFetchStats from 'modules/timeoff/hooks/useFetchStats'
import useForms from 'modules/timeoff/hooks/useForms'
import useLabourUnits from 'modules/timeoff/hooks/useLabourUnits'
import useModalProps from 'modules/timeoff/hooks/useModalProps'

import useCreateLeave from './useCreateLeave'
import useMedicalLeave from './useMedicalLeave'

interface Props {
  leaveTypeId?: string | null
  leaveYear?: string
  onClose: (year: number) => void
  startDate?: string | undefined
  endDate?: string | undefined
  fromShifts?: boolean
  customResourceFieldsData: ResourceFieldDataFragment[]
  currentEmployee: TimeoffCurrentEmployeeDataFragment
  leaveEmployee: LeaveEmployeeDataFragment
  sickFolderId: string
  leaveCanManageLeaveDocuments: boolean
  approvalGroupMemberships: Array<{
    id: string
    approvalGroup?: { id: string; autoApproved: boolean } | null
  }>
}

const NewLeaveModal = ({
  leaveTypeId,
  leaveYear,
  onClose,
  startDate,
  endDate,
  fromShifts = false,
  customResourceFieldsData,
  currentEmployee,
  leaveEmployee,
  sickFolderId,
  leaveCanManageLeaveDocuments,
  approvalGroupMemberships,
}: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()

  const fetchLeaveTypes = useFetchLeaveTypes({
    leaveTypeId: leaveTypeId !== undefined ? leaveTypeId : undefined,
    loadingLeave: false,
    employeeId: leaveEmployee.id,
    employeeTimeoffPolicyId: leaveEmployee.timeoffPolicyId,
    policyTemporality: hasFeature('timeoff_policy_timeline'),
    active: true,
  })

  const { leaveTypes, isLoading: isLeaveTypesLoading } = fetchLeaveTypes
  let { selectedLeaveType } = fetchLeaveTypes

  const { form, documentForm, year } = useForms({
    leaveYear,
    selectedLeaveType,
    leaveEmployeeId: leaveEmployee.id,
    startDate,
    endDate,
  })

  const leaveId = form.get('leave_type_id').value
  const leaveType = leaveTypes.get(leaveId)
  const allowance = leaveType?.getAllowanceForEmployee(leaveEmployee.timeoffPolicyId)

  const onCloseYear = () => {
    onClose(year)
  }

  const employeeRole = useCurrentEmployeeRole({
    form,
    canApproveLeaves: currentEmployee._policies?.approveLeaves.can ?? false,
    hasTimeoffManager: !!leaveEmployee.timeoffManager,
  })

  const labourUnits = useLabourUnits({
    form,
    employeeTimeoffPolicyId: leaveEmployee.timeoffPolicyId,
  })

  const { fetchStats } = useFetchStats()

  const customFields = useCustomFields({
    customResourceFieldsData,
  })

  const medicalLeave = useMedicalLeave({
    form,
    employeeId: leaveEmployee.id,
    leaveTypes,
  })

  const { onSubmit, isProcessing } = useCreateLeave({
    leaveEmployeeId: leaveEmployee.id,
    form,
    documentForm,
    getFieldsValues: customFields.getValues,
    onLeaveSuccess: medicalLeave.fetchUpdates,
    onLeaveError: (_error) => {
      if (medicalLeave.isConfirming) {
        medicalLeave.onBackConfirm()
      }
    },
    fetchStats,
    onCloseYear,
    sickFolderId,
  })

  const isLoading = isProcessing || isLeaveTypesLoading

  const modalProps = useModalProps({
    form,
    canApproveLeaves: currentEmployee._policies?.approveLeaves.can ?? false,
    timeoffManagerFullName: leaveEmployee.timeoffManager?.fullName ?? null,
    isMedical: medicalLeave.isMedical && medicalLeave.isConfirming,
    hasManualApprovalGroup: approvalGroupMemberships.some(
      (membership) => membership.approvalGroup?.autoApproved === false
    ),
  })

  const willAutoApprove = currentEmployee._policies?.approveLeaves.can ?? false

  const regularButton = useButtonCopy({
    form,
    isCurrentEmployee: currentEmployee.id === leaveEmployee.id,
    labourUnits,
    currentEmployeeRole: employeeRole,
  })

  const isSubmitEnabled = () => {
    const formLeaveTypeId = form.get('leave_type_id').value
    const halfDay = form.get('half_day').value
    const hourAmount = form.get('hours_amount_in_cents').value

    selectedLeaveType = leaveTypes.get(String(formLeaveTypeId))

    if (!selectedLeaveType) return true

    // Disable submit while day-type sync is pending: leave type is days-only but
    // form still has hourly fields set (avoids submitting hours for a days-only type).
    const isLeaveTypeHourly =
      selectedLeaveType.isHourlyForEmployee(leaveEmployee.timeoffPolicyId) ?? false
    if (
      !isLeaveTypeHourly &&
      (form.get('hours_amount_in_cents').value || form.get('start_time').value)
    ) {
      return false
    }

    const attachmentOptional = selectedLeaveType.isAttachmentOptional
    const medicalLeaveDocument = form.get('medical_leave_document').value
    const medicalLeaveDocumentFile = documentForm.get('file').value
    const isDetailsRequired = selectedLeaveType?.attributes.details_required
    const hasLeaveDescription = form.get('description').value
    const absurdStartTime = !form.startTimeHasValue && !halfDay && !!hourAmount
    const hasStartOnDate = form.get('start_on').value

    if (isDetailsRequired) {
      return selectedLeaveType?.isMedicalLeave
        ? hasLeaveDescription && (attachmentOptional || medicalLeaveDocument)
        : hasLeaveDescription && (attachmentOptional || medicalLeaveDocumentFile)
    }

    if (absurdStartTime) return false

    if (selectedLeaveType?.isMedicalLeave) {
      return attachmentOptional || medicalLeaveDocument
    }

    return hasStartOnDate && (attachmentOptional || medicalLeaveDocumentFile)
  }

  const { approvalFlows } = useAccessApprovalFlows('timeoff', currentEmployee.access.id)

  const canDelegateApprover =
    !hasFeature('dev_disable_delegate_approval') &&
    (currentEmployee._policies?.approveLeaves.can || approvalFlows.length > 0)

  const onSubmitTracked = () => {
    // The user will only get this error is he's been playing with the HTML
    // inspector and removed the disabled attribute from the submit button
    if (!isSubmitEnabled()) {
      return
    }
    track(trackingEvents.timeoff.addTimeOffSubmit, {
      from_shifts: fromShifts,
    })

    onSubmit()
  }

  const isDisabled = isLoading || !isSubmitEnabled()

  useEffect(() => {
    track(trackingEvents.timeoff.addTimeOffStart, {
      from_shifts: fromShifts,
    })
  }, [fromShifts])

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={() => onCloseYear()}>
      <Modal
        illustration={modalProps.illustration}
        title={modalProps.title}
        description={modalProps.description}
        onClose={() => onCloseYear()}
        onSubmit={onSubmitTracked}
        footer={
          <>
            {medicalLeave.isMedical && medicalLeave.isConfirming && (
              <Button
                type="secondary"
                onClick={medicalLeave.onBackConfirm}
                disabled={isLoading || !currentEmployee}
              >
                {i18n.t('common.back')}
              </Button>
            )}

            {medicalLeave.isMedical && !medicalLeave.isConfirming && (
              <Button
                onClick={medicalLeave.onConfirm}
                disabled={!currentEmployee || !isSubmitEnabled()}
              >
                {i18n.t('time_off.medical_leave.request')}
              </Button>
            )}

            {medicalLeave.isMedical && medicalLeave.isConfirming && (
              <Button disabled={isLoading || !currentEmployee} submit>
                {i18n.t('time_off.medical_leave.request_confirm')}
              </Button>
            )}
            {!medicalLeave.isMedical && !regularButton.tooltip && (
              <Button submit disabled={isDisabled}>
                {regularButton.button}
              </Button>
            )}

            {!medicalLeave.isMedical && !!regularButton.tooltip && (
              <Tooltip
                attachment="bottom center"
                targetAttachment="top center"
                type="brand"
                size="normal"
                open={regularButton.tooltipOpen}
                toggle={regularButton.toggleTooltip}
              >
                <Button disabled={isDisabled} submit>
                  {regularButton.button}
                </Button>
                <Tooltip.Pad>{regularButton.tooltip}</Tooltip.Pad>
              </Tooltip>
            )}
          </>
        }
      >
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Content
              customFieldsForms={customFields.forms}
              documentForm={documentForm}
              form={form}
              labourUnits={labourUnits}
              leaveEmployee={leaveEmployee}
              leaveTypes={leaveTypes}
              year={year}
              leaveCanManageLeaveDocuments={leaveCanManageLeaveDocuments}
            />
            <Box gap="s8">
              {allowance && (
                <FranceCalendarWarnings allowance={allowance} labourUnits={labourUnits} />
              )}
              <LeaveInfo form={form} employeeId={leaveEmployee.id} />
              {canDelegateApprover && (
                <DelegateApproverSelector
                  category="timeoff"
                  employeeAccessId={currentEmployee.access.id}
                  field={form.get('delegated_approver_id')}
                  startsOn={form.get('start_on').value}
                  finishOn={form.get('finish_on').value}
                />
              )}
              {willAutoApprove && (
                <Alert color="warning">
                  <Box flexDirection="row" justifyContent="center">
                    <Text.Normal>
                      {i18n.t('time_off.employee.add_new.auto_approval_flow')}
                    </Text.Normal>
                  </Box>
                </Alert>
              )}
            </Box>
          </>
        )}
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(NewLeaveModal)
